import { useEffect, useState } from 'react';
import { Loader2, Lock } from 'lucide-react';
import { APPLICATION_MODULE_SPINNER_CLASS } from '../constants';
import { OnScreenModal } from '@/components/ui/OnScreenModal';
import {
  getDenialReasons,
  filterLockedDenialCheckboxContent,
} from '../services/applicationService';
import type {
  DenialReasonsResponse,
} from '../types/application';
import styles from './denialReasonsModal.module.css';
import modalStyles from '../styles/modals.module.css';
import type { DenialReasonsModalProps, DenialCheckboxChild, DenialCheckboxSection } from '../types/components'



/** API shape for checkbox_content entries (see Angular denied-reason.component). */


export function DenialReasonsModal({
  applicantId,
  isOpen,
  onClose,
  denialEmailContentCallSite = 'adminApplication',
}: DenialReasonsModalProps) {
  const [loading, setLoading] = useState(false);
  const [denialReasons, setDenialReasons] = useState<DenialReasonsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && applicantId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- legitimate data-fetch-on-open effect (loading state for an async load); valid effect use per React docs
      setLoading(true);
      setError(null);
      getDenialReasons(Number(applicantId))
        .then((data) => {
          const processed =
            denialEmailContentCallSite === 'residentApplicationList'
              ? filterLockedDenialCheckboxContent(data)
              : data;
          setDenialReasons(processed);
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : 'Failed to load denial reasons');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setDenialReasons(null);
      setError(null);
    }
  }, [isOpen, applicantId, denialEmailContentCallSite]);

  if (!isOpen) return null;

  const renderContent = () => {
    if (loading) {
      return (
        <div
          className="flex flex-col items-center justify-center py-20"
          data-testid="application-modal-denial-reasons-loading"
        >
          <Loader2
            className={`mb-2 h-8 w-8 ${APPLICATION_MODULE_SPINNER_CLASS}`}
            data-testid="application-modal-denial-reasons-loading-spinner"
          />
          <span
            className="text-gray-500 text-base"
            data-testid="application-modal-denial-reasons-loading-text"
          >
            Loading details...
          </span>
        </div>
      );
    }

    if (error) {
      return (
        <div
          className="bg-red-50 border border-red-200 rounded-lg p-4 text-base text-red-800"
          data-testid="application-modal-denial-reasons-error"
        >
          <p data-testid="application-modal-denial-reasons-error-message">{error}</p>
        </div>
      );
    }

    if (!denialReasons) return null;

    const showDefaultTemplate = !denialReasons.checkbox_content && denialReasons.default_email_template;

    if (showDefaultTemplate) {
      return (
        <div
          className={`${styles.root} space-y-4 text-gray-700`}
          data-testid="application-modal-denial-reasons-default-template"
        >
          <p className={styles.defaultBlock} data-testid="application-modal-denial-reasons-default-template-greeting">
            <span style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
              Hello {denialReasons.firstName || ''} {denialReasons.lastName || ''},
            </span>
          </p>
          <p
            dir="ltr"
            className={styles.defaultBlock}
            data-testid="application-modal-denial-reasons-default-message"
          >
            <span style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
              Your application has been denied. Please Call our Customer Service team at 877-581-7368 for more information.
            </span>
          </p>
        </div>
      );
    }

    if (denialReasons.checkbox_content) {
      const rawSections = Object.entries(denialReasons.checkbox_content);
      const sectionsToRender = rawSections
        .map(([sectionKey, sectionRaw]) => {
          const section = sectionRaw as DenialCheckboxSection;
          const children: DenialCheckboxChild[] =
            section.child && Array.isArray(section.child) ? section.child : [];
          return { key: sectionKey, section, children };
        })
        .filter(({ children }) => children.length > 0);

      if (sectionsToRender.length === 0) {
        return (
          <div
            className={styles.emptyState}
            data-testid="application-modal-denial-reasons-checkbox-empty"
          >
            No denial details available.
          </div>
        );
      }

      const displayName = [denialReasons.firstName, denialReasons.lastName].filter(Boolean).join(' ') || 'Applicant';

      return (
        <div className={styles.root} data-testid="application-modal-denial-reasons-content">
          <div
            className={styles.greeting}
            data-testid="application-modal-denial-reasons-greeting"
          >
            <p className={`m-0 ${styles.innerHtmlReset}`}>Hello {displayName}</p>
          </div>

          <ul
            className={styles.sectionsList}
            data-testid="application-modal-denial-reasons-sections"
          >
            {sectionsToRender.map(({ key, section, children }) => (
              <li
                key={key}
                className={styles.section}
                data-testid={`application-modal-denial-reasons-section-${key}`}
              >
                <div
                  className={`${styles.sectionHeader} ${styles.innerHtmlReset}`}
                  data-testid={`application-modal-denial-reasons-section-${key}-header`}
                  dangerouslySetInnerHTML={{ __html: section.label }}
                />
                <ul
                  className={styles.reasonsList}
                  data-testid={`application-modal-denial-reasons-section-${key}-reasons`}
                >
                  {children.map((reason, index) => (
                    <li
                      key={`${key}-${index}`}
                      className={styles.reasonItem}
                      data-testid={`application-modal-denial-reasons-section-${key}-reason-${index}`}
                    >
                      <div
                        className={styles.reasonHeader}
                        data-testid={`application-modal-denial-reasons-section-${key}-reason-${index}-header`}
                      >
                        <div className={styles.feDeniedMain}>
                          <div
                            className={`min-w-0 flex-1 ${styles.innerHtmlReset}`}
                            data-testid={`application-modal-denial-reasons-section-${key}-reason-${index}-label`}
                            dangerouslySetInnerHTML={{ __html: reason.label }}
                          />
                          {reason.isLock === 1 && (
                            <Lock
                              className="mt-1 h-3.5 w-3.5 shrink-0 text-red-500"
                              data-testid={`application-modal-denial-reasons-section-${key}-reason-${index}-lock-icon`}
                              aria-hidden
                            />
                          )}
                        </div>
                      </div>
                      {reason.description && String(reason.description).trim().length > 0 && (
                        <ul
                          className={styles.descriptionList}
                          data-testid={`application-modal-denial-reasons-section-${key}-reason-${index}-description-list`}
                        >
                          <li
                            className={`${styles.descriptionItem} ${styles.innerHtmlReset}`}
                            data-testid={`application-modal-denial-reasons-section-${key}-reason-${index}-description`}
                            dangerouslySetInnerHTML={{ __html: reason.description }}
                          />
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      );
    }

    return (
      <div className={styles.emptyState} data-testid="application-modal-denial-reasons-empty">
        No denial details available.
      </div>
    );
  };

  return (
    <OnScreenModal
      isOpen={isOpen}
      title="Denial Reasons"
      onClose={onClose}
      maxWidth="max-w-xl"
      headerVariant="centeredTitle"
      overlayDataTestId="application-modal-denial-reasons-overlay"
      panelDataTestId="application-modal-denial-reasons-panel"
      titleDataTestId="application-modal-denial-reasons-title"
      closeButtonDataTestId="application-modal-denial-reasons-header-close-button"
      footer={
        <div
          className={modalStyles.modalFooterActionGroup}
          data-testid="application-modal-denial-reasons-footer"
        >
          <button
            type="button"
            className={modalStyles.modalActionPrimary}
            onClick={onClose}
            data-testid="application-modal-denial-reasons-close-btn"
          >
            Close
          </button>
        </div>
      }
    >
      <div data-testid="application-modal-denial-reasons-body-content">
        {renderContent()}
      </div>
    </OnScreenModal>
  );
}
